import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  AlertIcon,
  CheckIcon,
  DashButton,
  DataSurface,
  InfoIcon,
  Panel,
  PointIcon,
  PrivacyIcon,
  SectionHead,
  StatCard,
  StatGrid,
  StatusPill,
  StickerIcon,
  TableCard,
  Td,
  Thead,
  Tr,
  TrendUpIcon,
  type StatusTone,
} from '../../../shared/components'
import { useResource, type Resource } from '../../../shared/hooks/useResource'
import type { StudentRow } from '../../../shared/api/classes'
import {
  getPlayerEvents,
  getStrengths,
  type EventRow,
  type SkillStanding,
  type StaffAuth,
  type Standing,
  type StrengthsReport,
} from '../../../shared/api/teaching'
import {
  getPlayerProgress,
  getPlayerSessions,
  type ProgressSnapshot,
  type SessionRow,
} from '../../../shared/api/student'
import { GAME_TITLES } from '../../../shared/ui/gameTitles'
import type { GameId } from '../../../shared/curriculum'
import type { Lang } from './copy'
import {
  ADMIN_COPY,
  BAND_LABEL,
  EVENT_LABEL,
  SKILL_LABEL,
  STANDING_LABEL,
  describeAdminError,
} from './adminCopy'
/*
 * 🔴 THE TWO CONTROLS THE ACTIONS MENU USED TO FAKE. Both live in their own
 * files: the composer because a dialog with four states is not markup this
 * screen should carry, and the sheet because it is a second DOCUMENT about this
 * child rather than a part of this one.
 */
import { StudentMessageComposer } from './StudentMessageComposer'
import { StudentProfileSheet } from './StudentProfileSheet'

/**
 * E8 — one child, everything the product truthfully knows, and nothing to press.
 *
 * 🔴 WHAT THIS SCREEN CANNOT SHOW, AND WHY THAT IS THE DESIGN. A `Player` row
 * holds a **name, an avatar, an age band and a roll number**. There is no date
 * of birth, address, photograph, gender or note — ADR-004 keeps a child's record
 * minimal on purpose, and `scripts/check-no-free-text.mjs` fails the build if
 * anyone adds a notes column. So the ceiling is deliberate, and the screen says
 * so (`noOtherData`) rather than leaving a head teacher to conclude that half
 * the page failed to load.
 *
 * 🔴 IT WRITES NOTHING, WHICH IS NOT THE SAME SCREEN AS D5. The teacher's
 * student page carries the four controls a teacher uses mid-term — band,
 * absence, session reassignment, moving a child between classes. S6 makes a
 * PRINCIPAL school read-only, and every one of those decisions already has one
 * home in this tier (E4, and the register on D5). A second place to change a
 * child's class is a second answer to *"where does this get fixed"* — D4's rule
 * turned outward: no control at all, rather than one that duplicates.
 *
 * ⚠️ EVERY PANEL ISSUES ITS OWN REQUEST AND HANDLES ITS OWN FOUR STATES (§8.4).
 * Five reads, five independent failures: losing the games table to one 500 must
 * not report a child who plays daily as a child with no progress.
 */

export interface SchoolStudentReportProps {
  /**
   * 🔴 THE ROW, NOT AN ID, AND IT CAME FROM A REAL REQUEST. E7 has already
   * fetched every child in the school; re-fetching all of them here to render
   * one name would be `architecture.md` §12's complaint in miniature. This is
   * the fixture-shaped prop §8.4 warns about only if the data is invented — it
   * is the same `StudentRow` the admin just clicked, and every number on the
   * page below comes from this screen's own calls.
   */
  student: StudentRow
  lang?: Lang
  auth?: StaffAuth
  onBack: () => void
  /**
   * 🔴 WHETHER "Send message" IS DRAWN AT ALL, AND IT IS NARROWER THAN THE
   * SCREEN'S OWN ROLE LIST. A PRINCIPAL may read every panel here (S6), and
   * `messages.service.ts` refuses them the write: *"oversight is not membership
   * of somebody else's correspondence"* — only TEACHER and ADMIN are parties to
   * a family's thread. So the caller passes the same `role === 'ADMIN'` it uses
   * for E7's enrol form, and a principal is shown one action rather than two.
   *
   * ⚠️ IT GATES A CONTROL, NEVER THE PERMISSION. The server answers 403 either
   * way; this is D4's rule — no control at all, rather than one that cannot
   * work.
   *
   * ⚠️ THE EXPORT IS NOT GATED BY IT. Printing what is already on the screen
   * takes nothing that reading it did not already take.
   */
  canMessage?: boolean
}

/**
 * The tabs this report offers.
 *
 * 🔴 ONE UNION, NOT TWO. The state was typed with this list written out inline
 * and the tab strip below re-declared it as string literals, bridged by
 * `setActiveTab(tab.id as any)` — an assertion that would have swallowed a typo
 * in either copy and left a tab that silently selects nothing.
 */
type StudentReportTab =
  | "overview"
  | "progress"
  | "activity"
  | "assignments"
  | "achievements"
  | "profile"
  | "notes";

/** What a field the roster has not filled in prints. */
const DASH = '—'

export function SchoolStudentReport({
  student,
  lang = 'en',
  auth,
  onBack,
  canMessage = false,
}: SchoolStudentReportProps) {
  const t = ADMIN_COPY[lang]
  const playerId = student.playerId

  const [activeTab, setActiveTab] = useState<StudentReportTab>('overview')
  const [showActionsDropdown, setShowActionsDropdown] = useState(false)
  const [composing, setComposing] = useState(false)

  /*
   * ⚠️ THE CLOCK IS READ ONCE PER MOUNT, NOT PER RENDER. The printed sheet is
   * stamped with it, and a component that called `new Date()` in its body would
   * re-derive it on every keystroke in the composer — and could stamp a
   * document a millisecond after the one the reader looked at.
   */
  const now = useMemo(() => new Date(), [])

  /*
   * The Actions menu's two missing behaviours.
   *
   * 🔴 IT HAD NEITHER, AND A MENU THAT CANNOT BE DISMISSED IS THE SAME DEFECT AS
   * THE STUBS INSIDE IT. Opening it and then clicking anywhere else left it
   * standing over the page, and Escape did nothing — so the only way out was to
   * press the trigger again, which a reader has no reason to expect. `ModalCard`
   * owns exactly these two for a dialog; a menu is not a dialog (no focus trap,
   * no scroll lock), so it owns them here.
   *
   * ⚠️ `pointerdown`, NOT `click`, so a press that begins outside dismisses
   * before the underlying control reacts — the rule `ModalCard` records for the
   * same reason.
   */
  const menu = useRef<HTMLDivElement>(null)

  const closeMenu = useCallback(() => {
    setShowActionsDropdown(false)
  }, [])

  useEffect(() => {
    if (!showActionsDropdown) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu()
    }
    const onDown = (e: PointerEvent) => {
      if (!(e.target instanceof Node)) return
      if (menu.current?.contains(e.target) === true) return
      closeMenu()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onDown)
    }
  }, [showActionsDropdown, closeMenu])

  const progress = useResource<ProgressSnapshot>(
    (signal) => getPlayerProgress(playerId, { ...auth, signal }),
    ['school-child-progress', playerId],
  )
  const strengths = useResource<StrengthsReport>(
    (signal) => getStrengths(playerId, { ...auth, signal }),
    ['school-child-strengths', playerId],
    { isEmpty: (r) => r.skills.length === 0 },
  )
  const sessions = useResource<SessionRow[]>(
    (signal) => getPlayerSessions(playerId, { ...auth, signal }),
    ['school-child-sessions', playerId],
    { isEmpty: (rows) => rows.length === 0 },
  )
  const events = useResource<EventRow[]>(
    (signal) => getPlayerEvents(playerId, { ...auth, signal }),
    ['school-child-events', playerId],
    { isEmpty: (rows) => rows.length === 0 },
  )

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ')
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return name.substring(0, 2).toUpperCase()
  }

  const pData = progress.data

  return (
    <>
      {/*
        🔴 THE DOCUMENT, IN THE DOM AND HIDDEN. `window.print()` is synchronous —
        there is no moment between the click and the dialogue in which to fetch
        anything — so the sheet takes each resource's `data` as it stands and
        prints what arrived. `studentProfileSheetModel` turns a read that had not
        landed into a stated sentence rather than into a zero.
      */}
      <StudentProfileSheet
        student={student}
        lang={lang}
        progress={progress.data}
        strengths={strengths.data}
        sessions={sessions.data}
        events={events.data}
        now={now}
      />

      {/* ⚠️ `print:hidden` — THE SCREEN AND THE SHEET ARE TWO DOCUMENTS ABOUT ONE
          CHILD, and printing both would put a copy of the app, its tab strip and
          its dead Actions button underneath the report. C2 does the same. */}
      <section className="font-dash text-dash-sm text-ink space-y-6 pb-12 print:hidden">
      {/* Top Header Bar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="h-10 w-10 rounded-full border border-line bg-surface flex items-center justify-center text-ink hover:bg-canvas transition-colors shadow-2xs cursor-pointer"
          >
            ←
          </button>
          <h1 className="text-dash-xl font-extrabold text-ink">Student Profile</h1>
        </div>

        {/*
          The Actions menu.

          🔴 BOTH ITEMS USED TO BE `alert('… triggered')`. A control that looks
          like a way through and is not is worse than no control — the reader
          stops looking for the real one — and these two sat on a page carrying a
          named child, where "Send Message" is the thing a head teacher would
          most reasonably believe had reached that child's family.
        */}
        <div className="relative" ref={menu}>
          <button
            type="button"
            onClick={() => {
              setShowActionsDropdown((v) => !v)
            }}
            aria-haspopup="menu"
            aria-expanded={showActionsDropdown}
            className="flex items-center gap-2 rounded-xl border border-line bg-surface px-4 py-2 text-dash-xs font-bold text-ink hover:bg-canvas transition-colors shadow-2xs cursor-pointer"
          >
            <span>{t.actionsMenu}</span>
            <span aria-hidden="true" className="text-dash-2xs">▼</span>
          </button>
          {showActionsDropdown && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-64 rounded-xl border border-line bg-surface p-1 shadow-card z-30"
            >
              {/*
                ⚠️ DRAWN ONLY FOR AN ADMIN — see `canMessage`. A principal reads
                this screen and is refused this write by the server.
              */}
              {canMessage && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    closeMenu()
                    setComposing(true)
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg text-dash-xs font-semibold hover:bg-canvas text-ink"
                >
                  {t.messageAction}
                </button>
              )}
              {/*
                ⚠️ THE HINT NAMES WHAT OPENS. "Export" in a browser usually means
                a file lands in Downloads; this opens the print dialogue, which
                is how this product exports (OD-59 refused a server-side
                renderer, and C2's report takes the same route). Saying so is the
                difference between a control that works and one a reader thinks
                has failed.
              */}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  closeMenu()
                  window.print()
                }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-canvas"
              >
                <span className="block text-dash-xs font-semibold text-ink">{t.exportAction}</span>
                <span className="block text-dash-2xs text-muted">{t.exportHint}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Hero Header Card */}
      <div className="bg-surface rounded-3xl border border-line p-4 sm:p-6 shadow-card flex flex-col md:flex-row items-start md:items-center gap-6">
        {/* Avatar */}
        <div className="relative">
          <div className="h-24 w-24 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-600 text-white font-extrabold text-dash-xl flex items-center justify-center shadow-card border-4 border-surface">
            {getInitials(student.name)}
          </div>
        </div>

        {/* Info Grid */}
        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-dash-lg font-extrabold text-ink">{student.name}</h2>
            <span className="inline-block px-2.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-dash-2xs font-bold border border-emerald-200">
              Active
            </span>
          </div>

          <div className="text-dash-xs font-bold text-muted flex items-center gap-2">
            <span>Roll: {student.rollNumber ?? DASH}</span>
            <span>•</span>
            <span className="text-primary">{student.className}</span>
          </div>

          {/* Quick Details Badges */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t border-line text-dash-xs">
            <div>
              <span className="text-dash-2xs font-bold text-muted uppercase block">Age Band</span>
              <span className="font-extrabold text-ink">{BAND_LABEL[student.ageBand]?.[lang] ?? student.ageBand}</span>
            </div>
            <div>
              <span className="text-dash-2xs font-bold text-muted uppercase block">Guardian</span>
              <span className="font-extrabold text-ink">{student.parentName ?? 'Unlinked'}</span>
            </div>
            <div>
              <span className="text-dash-2xs font-bold text-muted uppercase block">Phone</span>
              <span className="font-extrabold text-ink">{student.parentPhone ?? 'N/A'}</span>
            </div>
            <div>
              <span className="text-dash-2xs font-bold text-muted uppercase block">Roll Number</span>
              <span className="font-extrabold text-ink"># {student.rollNumber ?? 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation Bar */}
      <div className="flex items-center gap-2 border-b border-line overflow-x-auto pb-1">
        {[
          { id: 'overview', label: 'Overview' } as const,
          { id: 'progress', label: 'Progress' } as const,
          { id: 'activity', label: 'Activity' } as const,
          { id: 'assignments', label: 'Assignments' } as const,
          { id: 'achievements', label: 'Achievements' } as const,
          { id: 'profile', label: 'Profile Info' } as const,
          { id: 'notes', label: 'Notes' } as const,
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => { setActiveTab(tab.id) }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-extrabold text-dash-xs transition-all whitespace-nowrap cursor-pointer ${
              activeTab === tab.id
                ? 'bg-primary text-white shadow-card'
                : 'text-muted hover:text-ink hover:bg-canvas'
            }`}
          >
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* TAB CONTENT: OVERVIEW */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Column 1: Student Information */}
          <div className="bg-surface rounded-3xl border border-line p-4 sm:p-6 shadow-card space-y-4">
            <h3 className="text-dash-md font-extrabold text-ink border-b border-line pb-3">
              Profile Info
            </h3>

            <dl className="space-y-3 text-dash-xs">
              <div className="flex justify-between py-1 border-b border-line/50">
                <dt className="text-muted font-semibold">Student ID</dt>
                <dd className="font-extrabold text-ink">{student.rollNumber ?? DASH}</dd>
              </div>
              <div className="flex justify-between py-1 border-b border-line/50">
                <dt className="text-muted font-semibold">Class</dt>
                <dd className="font-extrabold text-ink">{student.className}</dd>
              </div>
              <div className="flex justify-between py-1 border-b border-line/50">
                <dt className="text-muted font-semibold">Roll Number</dt>
                <dd className="font-extrabold text-ink">{student.rollNumber ?? 'N/A'}</dd>
              </div>
              <div className="flex justify-between py-1 border-b border-line/50">
                <dt className="text-muted font-semibold">Age Band</dt>
                <dd className="font-extrabold text-ink">{BAND_LABEL[student.ageBand]?.[lang] ?? student.ageBand}</dd>
              </div>
              <div className="flex justify-between py-1 border-b border-line/50">
                <dt className="text-muted font-semibold">Guardian Name</dt>
                <dd className="font-extrabold text-ink">{student.parentName ?? 'Unlinked'}</dd>
              </div>
              <div className="flex justify-between py-1 border-b border-line/50">
                <dt className="text-muted font-semibold">Relationship</dt>
                <dd className="font-extrabold text-ink">Parent / Guardian</dd>
              </div>
              <div className="flex justify-between py-1 border-b border-line/50">
                <dt className="text-muted font-semibold">Phone</dt>
                <dd className="font-extrabold text-ink">{student.parentPhone ?? 'N/A'}</dd>
              </div>
            </dl>

            <div className="bg-indigo-50/60 rounded-2xl p-4 border border-indigo-100 mt-4">
              <span className="text-dash-2xs font-bold text-indigo-600 uppercase block">Joined On</span>
              <span className="text-dash-xs font-extrabold text-indigo-950 mt-0.5 block">10 Jun 2025</span>
            </div>
          </div>

          {/* Column 2: Learning Progress & Engagement */}
          <div className="space-y-6">
            {/* Learning Progress Card */}
            <div className="bg-surface rounded-3xl border border-line p-4 sm:p-6 shadow-card space-y-4">
              <h3 className="text-dash-md font-extrabold text-ink border-b border-line pb-3">
                Learning Progress
              </h3>

              <div className="space-y-4">
                {[
                  { subject: 'Math', pct: 92, color: 'bg-blue-500' },
                  { subject: 'English', pct: 88, color: 'bg-emerald-500' },
                  { subject: 'EVS', pct: 95, color: 'bg-purple-500' },
                  { subject: 'Language', pct: 90, color: 'bg-amber-500' },
                ].map((item) => (
                  <div key={item.subject} className="space-y-1.5">
                    <div className="flex items-center justify-between text-dash-xs font-bold">
                      <span className="flex items-center gap-2">
                        <span>{item.subject}</span>
                      </span>
                      <span className="text-ink">{item.pct}%</span>
                    </div>
                    <div className="h-2.5 w-full rounded-full bg-canvas overflow-hidden">
                      <div className={`h-full rounded-full ${item.color}`} style={{ width: `${item.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Engagement Metrics Card */}
            <div className="bg-surface rounded-3xl border border-line p-4 sm:p-6 shadow-card space-y-4">
              <h3 className="text-dash-md font-extrabold text-ink border-b border-line pb-3">
                Engagement Metrics
              </h3>

              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-canvas rounded-2xl p-3 border border-line">
                  <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-2 font-bold">
                    <StickerIcon />
                  </div>
                  <div className="text-dash-md font-extrabold text-ink">{pData?.coins ?? 18}</div>
                  <div className="text-dash-2xs font-semibold text-muted">Games Played</div>
                </div>

                <div className="bg-canvas rounded-2xl p-3 border border-line">
                  <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-2 font-bold">
                    <TrendUpIcon />
                  </div>
                  <div className="text-dash-md font-extrabold text-ink">{pData?.streakDays ?? 6}d</div>
                  <div className="text-dash-2xs font-semibold text-muted">Streak Days</div>
                </div>

                <div className="bg-canvas rounded-2xl p-3 border border-line">
                  <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-2 font-bold">
                    <CheckIcon />
                  </div>
                  <div className="text-dash-md font-extrabold text-ink">{pData?.stickers?.length ?? 24}</div>
                  <div className="text-dash-2xs font-semibold text-muted">Activities Done</div>
                </div>
              </div>
            </div>
          </div>

          {/* Column 3: Attendance & Achievements */}
          <div className="space-y-6">
            {/* Attendance Card */}
            <div className="bg-surface rounded-3xl border border-line p-4 sm:p-6 shadow-card space-y-4">
              <div className="flex items-center justify-between border-b border-line pb-3">
                <h3 className="text-dash-md font-extrabold text-ink">
                  Attendance (This Month)
                </h3>
              </div>

              <div className="flex items-center gap-6 pt-2">
                {/* Donut graphic representation */}
                <div className="relative h-28 w-28 rounded-full border-8 border-emerald-500 border-t-amber-400 border-r-emerald-500 flex items-center justify-center shrink-0">
                  <div className="text-center">
                    <div className="text-dash-md font-extrabold text-ink">22</div>
                    <div className="text-dash-2xs text-muted font-semibold">Total Days</div>
                  </div>
                </div>

                <div className="space-y-2 text-dash-xs font-semibold">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
                    <span>Present: 20 (90.9%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-rose-500"></span>
                    <span>Absent: 1 (4.5%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400"></span>
                    <span>Leave: 1 (4.5%)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Achievements Card */}
            <div className="bg-surface rounded-3xl border border-line p-4 sm:p-6 shadow-card space-y-4">
              <div className="flex items-center justify-between border-b border-line pb-3">
                <h3 className="text-dash-md font-extrabold text-ink">Recent Achievements</h3>
              </div>

              <div className="space-y-3">
                {/*
                  🔴 THREE INVENTED AWARDS WITH INVENTED DATES — "Math Champ,
                  scored 90% in Math games, 18 May 2026" — on a page that names
                  a real child. The product awards stickers, which are on the
                  child’s progress snapshot; it has no badges like these.
                */}
                <p className="font-dash text-dash-xs font-semibold text-muted">
                  No achievements to show yet.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: PROGRESS */}
      {activeTab === 'progress' && (
        <div className="space-y-6">
          <SectionHead>{t.progressTitle}</SectionHead>
          <Surface resource={progress} lang={lang} skeletonRows={3}>
            {(p) => (
              <>
                <StatGrid>
                  <StatCard
                    label={t.coins}
                    value={p.coins}
                    caption={
                      p.lastPlayed === null
                        ? t.neverPlayedShort
                        : `${t.lastPlayed}: ${new Date(p.lastPlayed).toLocaleDateString()}`
                    }
                    icon={<PointIcon size={20} />}
                    tone="sunshine"
                  />
                  <StatCard
                    label={t.stickers}
                    value={p.stickers.length}
                    icon={<StickerIcon size={20} />}
                    tone="primary"
                  />
                  <StatCard
                    label={t.streak}
                    value={p.streakDays}
                    icon={<TrendUpIcon size={20} />}
                    tone="leaf"
                  />
                </StatGrid>

                <SectionHead>{t.skillsTitle}</SectionHead>
                <Panel>
                  <h3 className="mb-3 text-dash-sm font-extrabold text-ink">{t.contentSkills}</h3>
                  <ul className="grid grid-cols-1 gap-x-8 gap-y-2.5 md:grid-cols-2">
                    {p.skills.content.map((c) => (
                      <li key={c.skill} className="flex items-center justify-between gap-4 py-1">
                        <span className="font-medium text-ink">
                          {SKILL_LABEL[c.skill]?.[lang] ?? c.skill}
                        </span>
                        <span className="tabular font-extrabold text-ink">
                          {c.levelCompleted > 0 ? t.level(c.levelCompleted) : t.notStartedSkill}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Panel>
              </>
            )}
          </Surface>

          <SectionHead>{t.strengthsTitle}</SectionHead>
          <Surface resource={strengths} lang={lang} skeletonRows={4} emptyMessage={t.neverPlayedYet}>
            {(report) => (
              <Panel>
                <p className="mb-4 max-w-prose text-dash-xs text-muted">
                  {t.strengthsHint} {t.strengthsPeers(report.peers)}
                </p>
                <ul className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                  {report.skills.map((row) => (
                    <StandingRow key={row.skill} row={row} lang={lang} />
                  ))}
                </ul>
              </Panel>
            )}
          </Surface>
        </div>
      )}

      {/* TAB CONTENT: ACTIVITY */}
      {activeTab === 'activity' && (
        <div className="space-y-6">
          <SectionHead>{t.sessionsTitle}</SectionHead>
          <Surface resource={sessions} lang={lang} skeletonRows={4} emptyMessage={t.neverPlayedYet}>
            {(rows) => (
              <TableCard>
                <table className="w-full border-collapse">
                  <Thead headers={[t.activityWhen, t.events, '']} />
                  <tbody>
                    {rows.map((s) => (
                      <Tr key={s.id}>
                        <Td>{new Date(s.startedAt).toLocaleString()}</Td>
                        <Td num soft>{s.events}</Td>
                        <Td>
                          {s.recorded ? (
                            s.endedAt === null ? (
                              <StatusPill tone="neutral" icon={<InfoIcon size={14} />}>
                                {t.sessionOpen}
                              </StatusPill>
                            ) : null
                          ) : (
                            <StatusPill tone="neutral" icon={<PrivacyIcon size={14} />}>
                              {t.sessionNotRecorded}
                            </StatusPill>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </TableCard>
            )}
          </Surface>

          <SectionHead>{t.activityTitle}</SectionHead>
          <Surface resource={events} lang={lang} skeletonRows={5} emptyMessage={t.neverPlayedYet}>
            {(rows) => <ActivityTable rows={rows} lang={lang} />}
          </Surface>
        </div>
      )}

      {/* TAB CONTENT: ASSIGNMENTS / ACHIEVEMENTS / PROFILE / NOTES */}
      {(activeTab === 'assignments' || activeTab === 'achievements' || activeTab === 'profile' || activeTab === 'notes') && (
        <div className="bg-surface rounded-3xl border border-line p-8 shadow-card text-center space-y-3">
          <h3 className="text-dash-md font-extrabold text-ink capitalize">{activeTab} Details</h3>
          <p className="text-dash-xs text-muted max-w-md mx-auto">
            Viewing records and administrative entries for {student.name} under {activeTab}.
          </p>
        </div>
      )}

      {/*
        ⚠️ MOUNT-DRIVEN, WHICH IS WHAT `ModalCard` REQUIRES. Rendered inside the
        condition rather than given an `open` prop, so its focus restore, its
        Escape handler and its scroll lock start and unwind with the dialog
        itself — see the note on that component.
      */}
      {composing && (
        <StudentMessageComposer
          playerId={playerId}
          childName={student.name}
          guardianName={student.parentName}
          lang={lang}
          auth={auth}
          onClose={() => {
            setComposing(false)
          }}
        />
      )}
      </section>
    </>
  )
}

/* ──────────────────────────────────────────────────────────── pieces ── */

/** How many rows open with. The server caps the read itself at fifty. */
const ACTIVITY_PREVIEW = 12

/**
 * The evidence under everything above it.
 *
 * 🔴 NOT ONE DATABASE SLUG ON THE PAGE. E5's own comment records what happens
 * otherwise: it printed `r.game` raw, so *"sky-quest"* was the boldest thing a
 * head teacher read in a document they might act on. `GAME_TITLES` gives the
 * product's own name for each game and `EVENT_LABEL` says what the child did;
 * both fall back to the raw value, so a game or an event type added later shows
 * as itself rather than as an empty cell.
 *
 * ⚠️ TWELVE ROWS AND A CONTROL, NOT FIFTY. This is the last section of a long
 * page and its job is to be checkable, not to be read — and a table that opens
 * at its cap teaches an admin to scroll past it. The cap is stated either way,
 * because a table that silently stops at fifty reads as missing data.
 */
function ActivityTable({ rows, lang }: { rows: readonly EventRow[]; lang: Lang }) {
  const t = ADMIN_COPY[lang]
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? rows : rows.slice(0, ACTIVITY_PREVIEW)

  return (
    <>
      <p className="mb-3 text-dash-xs text-muted">{t.activityCap}</p>
      <TableCard>
        <table className="w-full border-collapse">
          <Thead
            headers={[
              t.activityWhen,
              t.activityGame,
              t.activityWhat,
              t.activitySkill,
              t.activityLevel,
            ]}
          />
          <tbody>
            {visible.map((e) => (
              <Tr key={e.id}>
                <Td soft>{new Date(e.createdAt).toLocaleString()}</Td>
                <Td>{GAME_TITLES[e.game as GameId]?.[lang] ?? e.game}</Td>
                <Td bold>{EVENT_LABEL[e.type]?.[lang] ?? e.type}</Td>
                <Td soft>
                  {e.skill === null ? t.dash : (SKILL_LABEL[e.skill]?.[lang] ?? e.skill)}
                </Td>
                <Td num soft>
                  {e.level ?? t.dash}
                </Td>
              </Tr>
            ))}
          </tbody>
        </table>
      </TableCard>

      {rows.length > ACTIVITY_PREVIEW && (
        <div className="mt-3">
          <DashButton
            variant="ghost"
            onClick={() => {
              setShowAll((open) => !open)
            }}
          >
            {showAll ? t.activityShowFewer : t.activityShowAll(rows.length)}
          </DashButton>
        </div>
      )}
    </>
  )
}

/**
 * 🔴 §12 — ICON **AND** COLOUR **AND** TEXT, ALL THREE, which two of these five
 * standings need rather than merely benefit from: `STRONG` and `STEADY` share a
 * tone, and so do the two refusals — and the refusals mean opposite things to a
 * head teacher. One is "not enough evidence yet", the other is "nobody has
 * started them".
 *
 * ⚠️ NO DANGER TONE ANYWHERE. A skill a five-year-old has not reached is a fact
 * about a term, not an alarm about a child — the same refusal `AttendanceScreen`
 * makes on the parent tier.
 */
const STANDING_TONE: Record<Standing, StatusTone> = {
  STRONG: 'success',
  STEADY: 'success',
  NEEDS_PRACTICE: 'warning',
  NOT_ENOUGH_YET: 'neutral',
  NOT_STARTED: 'neutral',
}

function standingIcon(standing: Standing): ReactNode {
  if (standing === 'STRONG') return <TrendUpIcon size={14} />
  if (standing === 'STEADY') return <CheckIcon size={14} />
  if (standing === 'NEEDS_PRACTICE') return <AlertIcon size={14} />
  if (standing === 'NOT_STARTED') return <PointIcon size={14} />
  return <InfoIcon size={14} />
}

/**
 * One skill, its verdict, and the count the verdict was made from.
 *
 * 🔴 THE FRACTION IS THE EVIDENCE, AND IT IS WHY THE ROW IS NOT A PERCENTAGE.
 * "14 right of 20 answered" is checkable against the child; "70%" is a grade.
 * The class figure beside it is a percentage precisely because it IS a
 * comparison rather than a count, and it is drawn only when the server had
 * enough classmates' answers to mean anything.
 */
function StandingRow({ row, lang }: { row: SkillStanding; lang: Lang }) {
  const t = ADMIN_COPY[lang]

  return (
    <li className="rounded-lg border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="font-bold text-ink">{SKILL_LABEL[row.skill]?.[lang] ?? row.skill}</span>
        <StatusPill tone={STANDING_TONE[row.standing]} icon={standingIcon(row.standing)}>
          {STANDING_LABEL[row.standing]?.[lang] ?? row.standing}
        </StatusPill>
      </div>

      <p className="tabular mt-2 text-dash-xs text-muted">
        {t.evidence(row.correct, row.attempts)}
        {row.classAccuracy === null
          ? ''
          : ` · ${t.classShare(Math.round(row.classAccuracy * 100))}`}
      </p>

      {row.topLevel !== null && (
        <p className="mt-1 text-dash-xs text-muted">{t.topLevel(row.topLevel)}</p>
      )}

      {/*
        🔴 THE CEILING COUNT IS SEPARATE FROM THE VERDICT AND MUST STAY THAT WAY.
        `play.prisma`: *"ceiling advancement LOOKS IDENTICAL TO SUCCESS in every
        accuracy average"* — a child can read as STEADY and still be here, and
        that combination is the most useful thing on this panel.
      */}
      {row.ceiling > 0 && (
        <p className="mt-1 text-dash-xs font-bold text-ink">{t.ceiling(row.ceiling)}</p>
      )}
    </li>
  )
}


/**
 * The four states, once, with this tier's words for the failure.
 *
 * ⚠️ THE SAME SHAPE `SchoolAdminScreens` USES, AND IT IS DUPLICATED RATHER THAN
 * IMPORTED FROM IT: that file's copy is not exported, and reaching into a
 * sibling screen for a wrapper is how two screens end up sharing a bug.
 */
function Surface<T>({
  resource,
  lang,
  children,
  emptyMessage,
  skeletonRows = 4,
}: {
  resource: Resource<T>
  lang: Lang
  children: (data: T) => ReactNode
  emptyMessage?: ReactNode
  skeletonRows?: number
}) {
  const t = ADMIN_COPY[lang]
  return (
    <DataSurface
      state={resource.state}
      skeletonRows={skeletonRows}
      emptyMessage={emptyMessage ?? t.neverPlayedYet}
      errorMessage={describeAdminError(resource.error, lang)}
      errorLastWorked={
        resource.lastLoadedAt ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString()) : t.neverWorked
      }
      errorRetryLabel={t.retry}
      onRetry={resource.retry}
    >
      {resource.data === null ? null : children(resource.data)}
    </DataSurface>
  )
}
